import { useState } from 'react';
import { ArrowUp, CornerDownLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useFleet } from '@/store/fleet';
import { useSelection } from '@/store/selection';
import { useSwarm } from '@/store/swarm';
import { FORMATIONS, type Formation } from '@/swarm/formations';
import { swarmRtl, swarmTakeoff } from '@/swarm/dispatch';

export function SwarmPanel() {
  const selection = useSelection((s) => s.selected);
  const states = useFleet((s) => s.states);
  const formation = useSwarm((s) => s.formation);
  const spacing = useSwarm((s) => s.spacing);
  const altStagger = useSwarm((s) => s.altStagger);
  const setFormation = useSwarm((s) => s.setFormation);
  const setSpacing = useSwarm((s) => s.setSpacing);
  const setAltStagger = useSwarm((s) => s.setAltStagger);
  const [takeoffAlt, setTakeoffAlt] = useState(10);

  if (selection.size < 2) return null;

  const ids = Array.from(selection);
  const inFlight = ids.filter(
    (id) => (states[id]?.position?.alt_rel_home_m ?? 0) > 0.5,
  ).length;

  return (
    <div className="space-y-3 rounded-lg border border-accent-soft/60 bg-accent-soft/15 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
        <Users className="h-3.5 w-3.5" />
        Swarm · {selection.size} drones · {inFlight} in flight
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Formation</Label>
          <Select value={formation} onValueChange={(v) => setFormation(v as Formation)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FORMATIONS.map((f) => (
                <SelectItem key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Spacing (m)</Label>
          <Input
            type="number"
            min={2}
            max={50}
            step={1}
            value={spacing}
            onChange={(e) => setSpacing(Number(e.target.value))}
          />
        </div>
        <div>
          <Label>Alt stagger (m)</Label>
          <Input
            type="number"
            min={0}
            max={10}
            step={0.5}
            value={altStagger}
            onChange={(e) => setAltStagger(Number(e.target.value))}
          />
        </div>
        <div>
          <Label>Takeoff alt</Label>
          <Input
            type="number"
            min={2}
            max={120}
            step={1}
            value={takeoffAlt}
            onChange={(e) => setTakeoffAlt(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" variant="primary" onClick={() => swarmTakeoff(ids, takeoffAlt)}>
          <ArrowUp className="h-3.5 w-3.5" />
          Swarm takeoff
        </Button>
        <Button size="sm" variant="secondary" onClick={() => swarmRtl(ids)}>
          <CornerDownLeft className="h-3.5 w-3.5" />
          Swarm RTL
        </Button>
      </div>

      <p className="text-xs text-fg-muted">
        Right-click the map to send a two-phase swarm goto in the selected formation.
      </p>
    </div>
  );
}

/** Saved formation in the swarm settings panel (currently only the picker). */
export function getSwarmFormation(): { formation: Formation; spacing: number; altStagger: number } {
  // Sprint 8: simple module-state fallback. Persistence to settings store is
  // a Sprint 9 follow-up. Default values.
  return { formation: 'line', spacing: 8, altStagger: 2 };
}
